<template>
  <div>
    <o-table :enable-new="false"
      :enable-edit="false"
      :title="$t('sandoz.ice_peak_contracts')"
      ref="tableList"
      url="/api/sandoz/ice-peak/contracts"
      default-sort-by-direction="desc"
      default-sort-by="id"
    >
      <template slot="filter">
        <o-filter v-on:filter-updated="(filters) => $refs.tableList.setFilters(filters)" :filter-cacheable="true">
          <o-filter-text :title="$t('sandoz.contract_no')" column="contract_no"></o-filter-text>
          <o-filter-text :title="$t('sandoz.customer_external_id')" column="customer_external_id"></o-filter-text>
          <o-filter-text :title="$t('sandoz.responsible_sales_person')" column="responsible_sales_person"></o-filter-text>
          <o-filter-select :items="status" :title="$t('sandoz.status')" column="status"></o-filter-select>
          <o-filter-select :items="years" :title="$t('sandoz.contract_year')" column="contract_year"></o-filter-select>
          <o-filter-checkbox :items="previousContract" :title="$t('sandoz.has_previous_contract')" column="no_previous_contract"></o-filter-checkbox>
          <o-filter-select :items="types" :title="$t('sandoz.contract_type')" column="type"></o-filter-select>
          <o-filter-text :title="$t('sandoz.company_name')" column="company_name"></o-filter-text>
          <o-filter-text :title="$t('sandoz.name')" column="name"></o-filter-text>
          <o-filter-text :title="$t('sandoz.address')" column="address" :placeholder="$t('sandoz.street_zip_city')"></o-filter-text>
        </o-filter>
      </template>
      <template slot="head">
        <o-table-header column="contract_no">{{$t('sandoz.contract_no')}}</o-table-header>
        <o-table-header column="customer_external_id">{{$t('sandoz.customer_external_id')}}</o-table-header>
        <o-table-header :sortable="false">{{$t('sandoz.company_name')}}</o-table-header>
        <o-table-header column="responsible_sales_person">{{$t('sandoz.responsible_sales_person')}}</o-table-header>
        <o-table-header :sortable="false">{{ $t('sandoz.next_approver_role') }}</o-table-header>
        <o-table-header column="status">{{$t('sandoz.status')}}</o-table-header>
      </template>
      <template slot="head-controls">
        <th class="text-right" v-if="hasPermission(['POST api/sandoz/ice-peak/contracts/{id}'])">
          <b-dropdown split :text="$t('core.new')" variant="primary" size="sm" @click="$router.push({ path: $route.path + '/new' })">
            <b-dropdown-item @click="showSelectContractModal">
              {{ $t('sandoz.create_from_existing') }}
            </b-dropdown-item>
          </b-dropdown>
        </th>
      </template>
      <template slot="body" slot-scope="props">
        <td>{{ props.item.contract_no }}</td>
        <td>{{ props.item.customer_external_id }}</td>
        <td>{{ props.item.company_name }}</td>
        <td>{{ props.item.responsible_sales_person }}</td>
        <td>
            <template v-if="props.item.is_approved">
                <template v-if="props.item.next_approver">
                    {{$t('sandoz.approved')}}
                    <a href="javascript:void(0);" @click="openApproverModal(props.item)">
                      <fa icon="info-circle" class="text-success"></fa>
                    </a>
                </template>
            </template>
            <template v-else-if="props.item.status !== 'Cancelled'">
                {{props.item.next_approver_role}}
                <a href="javascript:void(0);" v-if="props.item.next_approver_role" @click="openApproverModal(props.item)">
                  <fa icon="info-circle"></fa>
                </a>
            </template>
        </td>
        <td>
          <span v-if="props.item.status == 'Feedback' || props.item.status == 'Rejected'" class="text-danger">
            {{ $t('sandoz.' + (props.item.status.match(/[A-Z][a-z]+/g) || [props.item.status]).join("_").toLowerCase().replace(/\s+/g, '_')) }}
            <fa icon="info-circle" @click.prevent="showFeedback(props.item)"></fa>
          </span>
          <span v-else>
            {{ $t('sandoz.' + (props.item.status.match(/[A-Z][a-z]+/g) || [props.item.status]).join("_").toLowerCase().replace(/\s+/g, '_')) }}
          </span>
        </td>
      </template>
      <template slot="body-controls" slot-scope="props">
        <td class="text-right">
          <div class="btn-group btn-group-sm" role="group" aria-label="edit controls">
            <o-delete method="delete" :url="'/api/sandoz/ice-peak/contracts/' + props.item.id"
                      v-on:action-executed="$refs.tableList.search"></o-delete>
            <router-link :to="{ path: $route.path + '/' + props.item.id }" class="btn btn-primary"
                         v-if="(props.item.status === 'Draft' || props.item.status === 'Feedback') && hasPermission(['POST api/sandoz/ice-peak/contracts/{id}'])">
              <fa icon="pencil-alt"/>
            </router-link>
            <router-link :to="{ path: $route.path + '/view/' + props.item.id }" class="btn btn-primary" v-else>
              <fa icon="eye"/>
            </router-link>
          </div>
        </td>
      </template>
    </o-table>
    <approver-modal ref="approverModal" :contract="contract"/>

    <!-- Select Source Contract Modal -->
    <b-modal ref="selectContractModal" :title="$t('sandoz.select_source_contract')" size="xl" hide-footer>
      <!-- Step 1: Select source contract -->
      <template v-if="cloneStep === 1">
        <o-table :enable-new="false"
          :enable-edit="false"
          ref="sourceContractTable"
          url="/api/sandoz/ice-peak/contracts"
          default-sort-by-direction="desc"
          default-sort-by="id"
          :sync-page-title="false"
        >
          <template slot="filter">
            <o-filter v-on:filter-updated="(filters) => $refs.sourceContractTable.setFilters(filters)">
              <o-filter-text :title="$t('sandoz.customer_external_id')" column="customer_external_id"></o-filter-text>
              <o-filter-select :items="years" :title="$t('sandoz.contract_year')" column="contract_year"></o-filter-select>
              <o-filter-select :items="types" :title="$t('sandoz.contract_type')" column="type"></o-filter-select>
              <o-filter-text :title="$t('sandoz.company_name')" column="company_name"></o-filter-text>
            </o-filter>
          </template>
          <template slot="head">
            <o-table-header column="contract_no">{{$t('sandoz.contract_no')}}</o-table-header>
            <o-table-header column="customer_external_id">{{$t('sandoz.customer_external_id')}}</o-table-header>
            <o-table-header :sortable="false">{{$t('sandoz.company_name')}}</o-table-header>
            <o-table-header column="contract_year">{{$t('sandoz.contract_year')}}</o-table-header>
            <o-table-header column="type">{{$t('sandoz.contract_type')}}</o-table-header>
            <o-table-header column="status">{{$t('sandoz.status')}}</o-table-header>
          </template>
          <template slot="body" slot-scope="props">
            <td>{{ props.item.contract_no }}</td>
            <td>{{ props.item.customer_external_id }}</td>
            <td>{{ props.item.company_name }}</td>
            <td>{{ props.item.contract_year }}</td>
            <td>{{ props.item.type }}</td>
            <td>{{ $t('sandoz.' + (props.item.status.match(/[A-Z][a-z]+/g) || [props.item.status]).join("_").toLowerCase().replace(/\s+/g, '_')) }}</td>
          </template>
          <template slot="body-controls" slot-scope="props">
            <td class="text-right">
              <button class="btn btn-sm btn-primary" @click="selectSourceContract(props.item)">
                <fa icon="check"/> {{ $t('sandoz.select') }}
              </button>
            </td>
          </template>
        </o-table>
      </template>

      <!-- Step 2: Select contract type and year for the new contract -->
      <template v-if="cloneStep === 2">
        <b-alert show variant="info" class="mb-3">
          <strong>{{ $t('sandoz.source_contract_items') }}:</strong>
          {{ selectedSourceContract.contract_no }} ({{ selectedSourceContract.company_name }}, {{ selectedSourceContract.contract_year }})
        </b-alert>
        <b-row>
          <b-col md="4">
            <b-form-group :label="$t('sandoz.contract_type')">
              <b-form-select v-model="cloneTargetType" :options="cloneTypes">
                <template #first>
                  <b-form-select-option :value="null" disabled>-- {{ $t('sandoz.contract_type') }} --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group :label="$t('sandoz.contract_year')">
              <b-form-select v-model="cloneTargetYear" :options="years"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group :label="$t('sandoz.customer_external_id')">
              <b-input-group>
                <input v-model="cloneTargetExternalId" class="form-control" disabled type="text">
                <b-input-group-append>
                  <b-btn variant="primary" @click.prevent="openCloneAddressSearch">
                    <fa icon="ellipsis-h"/>
                  </b-btn>
                </b-input-group-append>
              </b-input-group>
              <small v-if="cloneTargetCompanyName" class="text-muted">{{ cloneTargetCompanyName }}</small>
            </b-form-group>
          </b-col>
        </b-row>
        <div class="d-flex justify-content-between mt-3">
          <b-button variant="secondary" @click="cloneStep = 1">
            <fa icon="arrow-left"/> {{ $t('core.back') }}
          </b-button>
          <b-button variant="primary" :disabled="!cloneTargetType" @click="confirmCloneSettings">
            {{ $t('sandoz.confirm_selection') }} <fa icon="arrow-right"/>
          </b-button>
        </div>
      </template>
    </b-modal>
    <address-search ref="cloneAddressSearch" contract-type="icepeak" @address-selected="cloneAddressSelected"></address-search>
  </div>
</template>


<script>
  import {mapGetters} from 'vuex'
  import axios from 'axios'
  import ApproverModal from '../partials/ApproverModal.vue'
  import AddressSearch from '../partials/AddressSearch.vue'
  import { contractYears } from '../../../utils/contractYear'

  export default {
    components: {
      ApproverModal,
      AddressSearch
    },
    data: () => ({
      status: [],
      years: [],
      types: [],
      previousContract: [],
      contract: null,
      cloneStep: 1,
      selectedSourceContract: null,
      cloneTargetType: null,
      cloneTargetYear: new Date().getFullYear(),
      cloneTargetExternalId: null,
      cloneTargetCompanyName: null
    }),
    created() {
      this.years = contractYears()

      this.status = [
        {value: 'Planned', text: this.$i18n.t('sandoz.planned')},
        {value: 'Draft', text: this.$i18n.t('sandoz.draft')},
        {value: 'Committed', text: this.$i18n.t('sandoz.committed')},
        {value: 'Signed', text: this.$i18n.t('sandoz.signed')},
        {value: 'Lost', text: this.$i18n.t('sandoz.lost')},
        {value: 'Cancelled', text: this.$i18n.t('sandoz.cancelled')},
        {value: 'ZeroInvest', text: this.$i18n.t('sandoz.zero_invest')},
        {value: 'Feedback', text: this.$i18n.t('sandoz.feedback')},
        {value: 'Rejected', text: this.$i18n.t('sandoz.rejected')},
        {value: 'Approved', text: this.$i18n.t('sandoz.approved')}
      ]
      this.previousContract = [
        {value: 'Yes', text: this.$i18n.t('sandoz.yes') },
        {value: 'No', text: this.$i18n.t('sandoz.no')}
      ]

      this.types = [
        {value: 'APO', text: this.$i18n.t('sandoz.apo')},
        {value: 'CNS', text: this.$i18n.t('sandoz.cns')},
        {value: 'HEIM', text: this.$i18n.t('sandoz.heim')},
        {value: 'IMMUNO', text: this.$i18n.t('sandoz.immuno')},
        {value: 'ONCO', text: this.$i18n.t('sandoz.onco')},
        {value: 'SD', text: this.$i18n.t('sandoz.sd')},
        {value: 'SD_SORTALYS', text: this.$i18n.t('sandoz.sd_sortlays')},
        {value: 'CNS_SORTALYS', text: this.$i18n.t('sandoz.cns_sortlays')},
        {value: 'IMMUNO_SORTALYS', text: this.$i18n.t('sandoz.immuno_sortlays')},
        {value: 'BIOS_APO', text: this.$i18n.t('sandoz.bios_apo') },
        {value: 'HIGHSPEC_SORTALYS', text: this.$i18n.t('sandoz.highspec_sortalys')},
        {value: 'APO_OTC', text: this.$i18n.t('sandoz.apo_otc')},
        {value: 'APO_KUKO', text: this.$i18n.t('sandoz.apo_kuko')},
        {value: 'SD_KUKO', text: this.$i18n.t('sandoz.sd_kuko')},
        {value: 'IMMUNO_KUKO', text: this.$i18n.t('sandoz.immuno_kuko')},
        {value: 'HOSP_KUKO', text: this.$i18n.t('sandoz.hosp_kuko')},
        {value: 'CNS_KUKO', text: this.$i18n.t('sandoz.cns_kuko')},
      ]
    },
    computed: {
      ...mapGetters({
        user: 'auth/user'
      }),
      cloneTypes() {
        return this.types.filter(type => !type.value.endsWith('_KUKO'))
      }
    },
    methods: {
      hasPermission(item) {
        let result = true
        let permissions = item
        let user = this.user
        if (!user.is_super_user && permissions) {
          permissions.forEach((permission) => {
            if (!user.routePermissions.includes(permission)) {
              result = false
            }
          })
        }
        return result
      },
      openApproverModal(contract) {
        this.contract = contract
        this.$refs.approverModal.showDialog()
      },
      showFeedback(item) {
        window.alert(item.approval_feedback)
      },
      closeApproverModal() {
        this.contract = null
        this.$refs.approverModal.hideDialog()
      },
      showSelectContractModal() {
        this.cloneStep = 1
        this.selectedSourceContract = null
        this.cloneTargetType = null
        this.cloneTargetYear = new Date().getFullYear()
        this.cloneTargetExternalId = null
        this.cloneTargetCompanyName = null
        this.$refs.selectContractModal.show()
      },
      selectSourceContract(contract) {
        this.selectedSourceContract = contract
        this.cloneTargetType = contract.type
        this.cloneTargetExternalId = contract.customer_external_id
        this.cloneTargetCompanyName = contract.company_name
        this.cloneStep = 2
      },
      confirmCloneSettings() {
        this.$refs.selectContractModal.hide()
        this.$router.push({
          path: this.$route.path + '/new',
          query: {
            from: this.selectedSourceContract.id,
            type: this.cloneTargetType,
            year: this.cloneTargetYear,
            external_id: this.cloneTargetExternalId || undefined
          }
        })
      },
      openCloneAddressSearch() {
        this.$refs.cloneAddressSearch.showAddressSearchModal(this)
      },
      cloneAddressSelected(event) {
        const address = event.address
        this.cloneTargetExternalId = address.org_external_id || address.external_id
        this.cloneTargetCompanyName = address.org_full_name || address.full_name
      },
    }
  }
</script>
